import {
    Alignment, ArrayUtil, Color, DisplayType, DisplayValue, DOMUtil, getThemeColor, HorizontalAlignment, Logger,
    MixinUtil, ModelRow, NumberUtil, NumberWithUnit, NumberWithUnitPeerField, StringUtil
} from "@mcleod/core";
import { BlurListener } from "../../events/BlurEvent";
import { BorderType } from "../../base/BorderType";
import { CaptionedMixin } from "../../mixins/CaptionedMixin";
import { CaptionedMixinInterface } from "../../mixins/CaptionedMixinInterface";
import { CaptionLabel } from "../label/CaptionLabel";
import { ChangeListener } from "../../events/ChangeEvent";
import { ClearButtonVisible } from "../textbox/ClearButtonVisible";
import { Component } from "../../base/Component";
import { ComponentFactory } from "../../page/ComponentFactory";
import { ComponentPropDefinition, ComponentPropDefinitions, IdProps } from "../../base/ComponentProps";
import { ComponentTypes } from "../../base/ComponentTypes";
import { Cursor } from "../../base/Cursor";
import { DataSource, DataSourceMode } from "../../databinding/DataSource";
import { DesignerInterface } from "../../base/DesignerInterface";
import { DesignableObjectLogManager } from "../../logging/DesignableObjectLogManager";
import { DomEvent } from "../../events/DomEvent";
import { DropdownItem } from "../textbox/DropdownItem";
import { DropdownSelectionListener } from "../../events/DropdownSelectionEvent";
import { ExportColumn } from "../table/TableExport";
import { FocusListener } from "../../events/FocusEvent";
import { KeyListener } from "../../events/KeyEvent";
import { LabelProps } from "../label/LabelProps";
import { Panel } from "../panel/Panel";
import { PermissionsDefinition } from "../../base/PermissionsDefinition";
import { PrintableEvent } from "../../events/PrintableEvent";
import { PrintableLabel } from "../label/PrintableLabel";
import { PrintableMixin } from "../../mixins/PrintableMixin";
import { PrintableMixinInterface } from "../../mixins/PrintableMixinInterface";
import { UnitInputFieldPropDefinitions, UnitInputFieldProps } from "./UnitInputFieldProps";
import { UnitInputFieldStyles } from "./UnitInputFieldStyles";
import { Textbox, TextboxItemsSource } from "../textbox/Textbox";
import { TextboxProps } from "../textbox/TextboxProps";
import { TextboxUnit } from "./TextboxUnit";
import { TextboxNumber } from "./TextboxNumber";
import { TooltipSource } from "../../page/tooltips/TooltipSource";
import { TooltipSourceType } from "../../page/tooltips/TooltipSourceType";
import { ValidationResult } from "../../base/ValidationResult";
import { ValidationUtil } from "../../databinding/ValidationUtil";

enum InnerIds {
    TEXTBOX_UNIT = "textboxUnit",
    TEXTBOX_NUMBER = "textboxNumber",
}

/**
 * A UnitInputField is a component that contains the following
 * - A 'Number' textbox, which contains a numeric value.
 * - A 'Unit' textbox, which is a dropdown-style field (presented in the style of a button) that allows the user to
 *   define what type of number is presented in the number field.  The unit will often be unit-of-measure values,
 *   such as MI/KM for distance fields.
 */
export class UnitInputField extends Component implements UnitInputFieldProps {
    private _addlValidationCallback: (value: string, mode: DataSourceMode) => ValidationResult;
    private bothTextboxes: Textbox[];
    private _captionAlignment: Alignment.LEFT | Alignment.TOP;
    private _captionVisible: boolean;
    private _captionVisibleInsideTable: boolean;
    private _explicitDisplayType: DisplayType;
    private logger: Logger;
    private _numberClearButtonVisible: ClearButtonVisible;
    private _numberDisabledByServer: boolean;
    private _printableLabel: PrintableLabel;
    private textboxDiv: HTMLDivElement;
    private textboxUnit: Textbox;
    private textboxNumber: Textbox;
    private _unitAllowDropdownBlank: boolean;
    private _unitDisabledByServer: boolean;
    private _validationWarnings: string[];
    private validationWarningProps: Partial<LabelProps>;
    
    constructor(props: Partial<UnitInputFieldProps>) {
        super("div", props);
        this.createTextboxNumber();
        this.createTextboxUnit();
        this.bothTextboxes = [ this.textboxNumber, this.textboxUnit ];
        this.createCaptionLabel();
        this.createTextboxDiv();
        this.placeElements();
        this.width = this.getPropertyDefinitions().width.defaultValue;
        this.unitWidth = this.getPropertyDefinitions().unitWidth.defaultValue;
        this.setProps({ unitAllowDropdownBlank: false, ...props });
    }

    override syncInnerComponentIds() {
        this.captionLabel?.syncId();
        this.printableLabel?.syncId();
        this.syncTextboxDivId();
        this.setInnerComponentId(this.textboxUnit, InnerIds.TEXTBOX_UNIT);
        this.setInnerComponentId(this.textboxNumber, InnerIds.TEXTBOX_NUMBER);
    }

    private createTextboxNumber() {
        const props = { id: this.createInnerComponentId(InnerIds.TEXTBOX_NUMBER) };
        this.textboxNumber = new TextboxNumber(props, this);
    }

    private createTextboxUnit() {
        const props = { id: this.createInnerComponentId(InnerIds.TEXTBOX_UNIT) };
        this.textboxUnit = new TextboxUnit(props, this);
    }

    private createCaptionLabel() {
        this.captionLabel = new CaptionLabel(this);
    }

    private createTextboxDiv() {
        this.textboxDiv = document.createElement("div");
        this.textboxDiv.classList.add(UnitInputFieldStyles.textboxDiv);
        this.textboxDiv.style.overflow = "hidden";
    }

    private syncTextboxDivId() {
        if (this.textboxDiv != null) {
            this.textboxDiv.id = `${this.id}-textboxDiv`
        }
    }

    private placeElements() {
        this.textboxDiv.appendChild(this.textboxNumber._element);
        this.textboxDiv.appendChild(this.textboxUnit._element);
        this._element.appendChild(this.captionLabel._element);
        this._element.appendChild(this.textboxDiv);
    }

    //////////////////////////////////////////
    // Overall Component Properties/Methods //
    //////////////////////////////////////////
    public get align(): HorizontalAlignment {
        return this.textboxNumber.align;
    }

    public set align(value: HorizontalAlignment) {
        this.textboxNumber.align = value;
    }

    public get backgroundColor(): Color {
        return this.textboxNumber.backgroundColor;
    }

    public set backgroundColor(value: Color) {
        this.textboxNumber.backgroundColor = value;
    }

    // Borders are handled in two ways:
    // - Methods that change the border on all sides of the component are overridden so that we can apply the change to
    //   just the desired inner elements.
    // - Methods that change just one side/corner of the component are not overridden.  Instead, the
    //   getBorderPropTargets() method will return the appropriate inner elements to which the change should be applied.
    public override get borderRadius(): string | number {
        return super.borderRadius;
    }

    public override set borderRadius(value: string | number) {
        this._borderRadius = value;
        const cssString = DOMUtil.getSizeSpecifier(value);
        this.textboxNumber._element.style.borderBottomLeftRadius = cssString;
        this.textboxNumber._element.style.borderTopLeftRadius = cssString;
        this.textboxUnit._element.style.borderBottomRightRadius = cssString;
        this.textboxUnit._element.style.borderTopRightRadius = cssString;
    }

    public override get borderWidth(): number {
        return super.borderWidth;
    }

    public override set borderWidth(value: number) {
        this._borderWidth = value;
        this._borderBottomWidth = value;
        this._borderLeftWidth = value;
        this._borderRightWidth = value;
        this._borderTopWidth = value;
        this.setInnerElementBorders();
    }

    public override get borderType(): BorderType {
        return super.borderType;
    }

    public set borderType(value: BorderType) {
        this._borderType = value;
        this._borderBottomType = value;
        this._borderLeftType = value;
        this._borderRightType = value;
        this._borderTopType = value;
        this.setInnerElementBorders();
    }

    public override get borderColor(): Color {
        return super.borderColor;
    }

    public override set borderColor(value: Color) {
        this._borderColor = value;
        this._borderBottomColor = value;
        this._borderLeftColor = value;
        this._borderRightColor = value;
        this._borderTopColor = value;
        this.setInnerElementBorders();
    }

    private setInnerElementBorders() {
        this.setElementBorder([ this.textboxNumber._inputDiv, this.textboxUnit._inputDiv ], "Bottom");
        this.setElementBorder([ this.textboxNumber._inputDiv ], "Left");
        this.setElementBorder([ this.textboxNumber._inputDiv, this.textboxUnit._inputDiv ], "Top");
        this.setElementBorder([ this.textboxUnit._inputDiv ], "Right");
    }

    protected override getBorderPropTargets(sideOrCorner: string, _changedProperty: string): HTMLElement[] {
        switch (sideOrCorner.toLowerCase()) {
            case "bottomleft":
            case "left":
            case "topleft":
                return [ this.textboxNumber._inputDiv ];
            case "bottomright":
            case "right":
            case "topright":
                return [ this.textboxUnit._inputDiv ];
            case "bottom":
            case "top":
            default:
                return [ this.textboxNumber._inputDiv, this.textboxUnit._inputDiv ];
        }
    }

    updateBordersForFocus(focused: boolean) {
        if (focused === true) {
            this.textboxNumber._inputDiv.classList.add(UnitInputFieldStyles.textboxNumberFocused);
            this.textboxUnit._inputDiv.classList.add(UnitInputFieldStyles.textboxUnitFocused);
        } else {
            this.textboxNumber._inputDiv.classList.remove(UnitInputFieldStyles.textboxNumberFocused);
            this.textboxUnit._inputDiv.classList.remove(UnitInputFieldStyles.textboxUnitFocused);
        }
    }

    get boundRow(): any {
        return this.textboxNumber.boundRow;
    }

    set boundRow(value: any) {
        this.applyToBothTextboxes({ boundRow: value });
    }

    get caption(): string {
        return this.super_get_caption();
    }

    set caption(value: string) {
        if (this.captionValueMatches(value) === true) {
            return;
        }
        this.super_set_caption(value);

        if (typeof value === "string" && value.startsWith("{") && this._designer == null)
            this.captionLabel.text = null;
        else
            this.syncCaption();
    }

    get captionLabelProps(): Partial<LabelProps> {
        return {
            ...this.super_get_captionLabelProps(),
            height: 16,
            paddingBottom: 2
        };
    }

    set captionProps(value: Partial<LabelProps>) {
        this.captionLabel.setProps(value);
    }

    syncCaption(): void {
        this.captionLabel.text = this.getPrefixedCaption();
        if (this._designer == null && this.insideTableCell === true && this.captionVisibleInsideTable === false) {
            this.captionVisible = false;
            const currPlaceholder = this.numberPlaceholder;
            if (currPlaceholder == null || currPlaceholder === "" || currPlaceholder === "Required") {
                const mode = this.getCurrentDataSourceMode();
                if (this.required === true && mode !== DataSourceMode.SEARCH) {
                    this.numberPlaceholder = "Required";
                }
                else {
                    this.numberPlaceholder = "";
                }
            }
        }
    }

    get captionAlignment(): Alignment.LEFT | Alignment.TOP {
        if (this._captionAlignment == null)
            return Alignment.TOP;
        else
            return this._captionAlignment;
    }

    set captionAlignment(value: Alignment.LEFT | Alignment.TOP) {
        this._captionAlignment = value;
        if (value === Alignment.LEFT) {
            this._element.style.display = "flex";
            this._element.style.flexDirection = "row";
            this._element.style.alignItems = "center";
            this.captionLabel.marginRight = 4;
        }
        else {
            this._element.style.display = "unset";
            this._element.style.alignItems = "";
            this.captionLabel.marginRight = 0;
        }
    }

    get captionVisible(): boolean {
        return this._captionVisible ?? this.getPropertyDefinitions().captionVisible.defaultValue;
    }

    set captionVisible(value: boolean) {
        if (value === this._captionVisible) {
            return;
        }
        this._captionVisible = value;
        if (value) {
            this._element.insertBefore(this.captionLabel._element, this._element.firstChild);
        }
        else {
            this._element.removeChild(this.captionLabel._element);
        }
    }

    get captionVisibleInsideTable(): boolean {
        return this._captionVisibleInsideTable ?? this.getPropertyDefinitions().captionVisibleInsideTable.defaultValue;
    }

    set captionVisibleInsideTable(value: boolean) {
        this._captionVisibleInsideTable = value;
        this.syncCaption();
    }

    public get color(): Color {
        return this.textboxNumber.color;
    }

    public set color(value: Color) {
        this.textboxNumber.color = value;
    }

    public get cursor(): Cursor {
        return this.textboxNumber.cursor;
    }

    public set cursor(value: Cursor) {
        this.textboxNumber.cursor = value;
    }

    override get dataSource(): DataSource {
        return super.dataSource;
    }

    override set dataSource(value: DataSource) {
        super.dataSource = value;
        this.applyToBothTextboxes({ dataSource: value });
    }

    override dataSourceModeChanged(mode: DataSourceMode) {
        super.dataSourceModeChanged(mode);
        this.syncPrintable();
    }

    get displayType(): DisplayType {
        if (this._explicitDisplayType != null) {
            return this._explicitDisplayType;
        }
        if (this._boundField != null) {
            return this._boundField.displayType;
        }
        return null;
    }
    
    set displayType(value: DisplayType) {
        this._explicitDisplayType = value;
        this.syncForDisplayType();
    }

    private syncForDisplayType() {
        this.textboxNumber.displayType = DisplayType.DECIMAL;
        this.unitDropdownWidth = this.defaultUnitDropdownWidth;
    }

    public get _designer(): DesignerInterface {
        return super._designer;
    }

    public set _designer(value: DesignerInterface) {
        super._designer = value;
        this.applyToBothTextboxes({ _interactionEnabled: value == null });
    }

    override _applyEnabled(value: boolean): void {
        this.textboxNumber.enabled = value;
        this.textboxUnit.enabled = value;
        if (value) {
            this._element.removeAttribute("disabled");
        } else {
            this._element.setAttribute("disabled", "true");
        }
    }

    get exportRowData(): Map<string, any> {
        const result = new Map<string, any>();
        result.set(this.field, this.textboxNumber.text);
        result.set(this.unitField, this.textboxUnit.selectedItem.value);
        return result;
    }

    getExportColumn(field: string): ExportColumn {
        let heading = this.getDisplayLabel(field);
        if (field === this.unitField) {
            heading += " UM"
        }
        return {
            heading,
            type: null,
            alignment: null
        };
    }

    override get field(): string {
        return super.field;
    }

    override set field(value: string) {
        super.field = value;
        this.textboxNumber.field = value;
        this.textboxUnit.field = value + NumberWithUnitPeerField.UNIT;
        this.syncPrintableLabelText();
    }

    protected override _fieldBindingChanged(): void {
        super._fieldBindingChanged();
        this.syncForDisplayType();
        this.syncPrintableLabel();
    }

    // Override for methods like DataSource._rebuildComponentsByField()
    override getFieldNames(): string[] {
        return [ this.field, this.unitField ].filter(fieldName => !StringUtil.isEmptyString(fieldName));
    }

    public override showContextMenu(event: DomEvent | Event) {
        this.updateBordersForFocus(true);
        super.showContextMenu(event);
    }

    override focus() {
        if (this.printableLabel != null) {
            this.printableLabel._element.focus();
        } else {
            super.focus()
        }
    }

    protected getFocusTarget(): HTMLElement {
        return this.textboxNumber._input;
    }

    protected override getFontTargets(): HTMLElement[] {
        if (this.printable === true) {
            return [ this.printableLabel?._element ];
        }
        return [ this.textboxNumber._input, this.textboxUnit._input ];
    }

    get specialParent(): Component {
        return super.specialParent;
    }

    set specialParent(value: Component) {
        super.specialParent = value;
        this.syncCaption();
    }

    get maxHeight(): string | number {
        return super.maxHeight;
    }

    set maxHeight(value: string | number) {
        super.maxHeight = value;
        if (this.printableLabel != null) {
            this.printableLabel.maxHeight = this.getPrintableLabelMaxHeight();
        }
    }

    get printableLabel(): PrintableLabel {
        return this._printableLabel;
    }

    private set printableLabel(value: PrintableLabel) {
        this._printableLabel = value;
    }

    private syncPrintableLabel() {
        if (this.printableLabel != null) {
            this.printableLabel.displayType = this.textboxNumber.displayType;
            this.printableLabel.style.alignItems = this.textboxNumber._inputDiv.style.alignItems;
        }
    }

    syncPrintableLabelText() {
        if (this.printableLabel == null) {
            return;
        }
        if (this._designer != null) {
            this.printableLabel.text = `${this.field}/${this.unitField}`;
            return;
        }
        const numberText = StringUtil.isEmptyString(this.textboxNumber.text) ? "--" : this.textboxNumber.text;
        const unitTest = StringUtil.isEmptyString(this.textboxUnit.text) ? "" : this.textboxUnit.text;
        this.printableLabel.text = `${numberText} ${unitTest}`;
    }

    protected _applyPrintable(value: boolean) {
        if (value === true) {
            if (this._element.contains(this.textboxDiv)) {
                this._element.removeChild(this.textboxDiv);
            }
            if (this.printableLabel == null) {
                this.createPrintableLabel();
                this._element.appendChild(this.printableLabel._element);
            }
            if (this.captionLabel != null) {
                this.captionLabel.paddingLeft = 0;
                if (this._captionAlignment === Alignment.LEFT) {
                    this.captionLabel.padding = null;
                    this.captionLabel.marginRight = 0;
                }
            }
        }
        else if (this.printableLabel != null) {
            if (this.captionLabel != null) {
                this.captionLabel.paddingLeft = 2;
            }
            this._element.removeChild(this.printableLabel._element);
            this._element.appendChild(this.textboxDiv);
            this.printableLabel = null;
        }
        this.firePrintableListeners(new PrintableEvent(this, this.printableLabel));
    }

    private createPrintableLabel() {
        this.printableLabel = new PrintableLabel(this, {
            padding: 0,
            fontSize: this.fontSize,
            fontBold: this.fontBold,
            color: this.color,
            minWidth: this.minWidth,
            width: this.width,
            fillRow: this.fillRow,
            maxHeight: this.getPrintableLabelMaxHeight(),
            maxWidth: this.maxWidth
        });
        this.syncPrintableLabel();
        this.syncPrintableLabelText();
    }

    private getPrintableLabelMaxHeight(): string | number {
        if (this.maxHeight == null) {
            return this.maxHeight;
        }
        const maxHeightInt = DOMUtil.getStyleAttrAsNumber(this.maxHeight);
        let captionLabelHeight = 0;
        if (this.isOrContains(this.captionLabel)) {
            captionLabelHeight = DOMUtil.getStyleAttrAsNumber(this.captionLabel.height);
        }
        return maxHeightInt - captionLabelHeight - captionLabelHeight;
    }

    override get required(): boolean {
        return super.required;
    }

    override set required(value: boolean) {
        super.required = value;
        this.applyToBothTextboxes({ required: value });
    }

    override get requiredDuringAdd(): boolean {
        return super.requiredDuringAdd;
    }

    override set requiredDuringAdd(value: boolean) {
        super.requiredDuringAdd = value;
        this.applyToBothTextboxes({ requiredDuringAdd: value });
    }

    override get requiredDuringSearch(): boolean {
        return super.requiredDuringSearch;
    }

    override set requiredDuringSearch(value: boolean) {
        super.requiredDuringSearch = value;
        this.applyToBothTextboxes({ requiredDuringSearch: value });
    }

    override get requiredDuringUpdate(): boolean {
        return super.requiredDuringUpdate;
    }

    override set requiredDuringUpdate(value: boolean) {
        super.requiredDuringUpdate = value;
        this.applyToBothTextboxes({ requiredDuringUpdate: value });
    }

    override get searchOnly(): boolean {
        return this.textboxNumber.searchOnly;
    }

    override set searchOnly(value: boolean) {
        this.applyToBothTextboxes({ searchOnly: value });
    }

    public override get tooltipAnchor(): HTMLElement {
        if (this.printable) {
            return this.printableLabel?._element;
        }
        return this.textboxDiv;
    }

    public override get tooltipSources(): TooltipSource[] {
        const result = super.tooltipSources;
        if (this._designer == null) {
            result.push(this.validationWarningTooltipSource);
        }
        return result;
    }

    private get validationWarningTooltipSource(): TooltipSource {
        const panelFunction = (): Component => {
            const numWarnings = ArrayUtil.getLength(this.validationWarnings);
            if (numWarnings === 0) {
                return null;
            }
            if (this.validationWarnings.length === 1) {
                return ComponentFactory.createLabel(ArrayUtil.getFirstElement(this.validationWarnings));
            }
            const errorPanel = Panel.createNoMarginNoPaddingPanel();
            const bulletedLabels = ComponentFactory.createBulletedList(this.validationWarnings);
            errorPanel.add(...bulletedLabels);
            return errorPanel;
        }
        return new TooltipSource(TooltipSourceType.VALIDATION_WARNING, panelFunction);
    }

    override styleForTooltip() {
        this.printableLabel?.styleForTooltip();
    }

    // Override so only the overall NumberWithUnitComponent component can exist as a bound component
    override displayComponentData(data: ModelRow, _allData: ModelRow[], _rowIndex: number) {
        let numberWithUnit: NumberWithUnit = null;
        if (data != null) {
            switch (this.displayType) {
                case DisplayType.DISTANCE:
                    numberWithUnit = data.getDistance(this.field);
                    break;
                case DisplayType.LENGTH:
                    numberWithUnit = data.getLength(this.field);
                    break;
                case DisplayType.TEMPERATURE:
                    numberWithUnit = data.getTemperature(this.field);
                    break;
                case DisplayType.VOLUME:
                    numberWithUnit = data.getVolume(this.field);
                    break;
                case DisplayType.WEIGHT:
                    numberWithUnit = data.getWeight(this.field);
                    break;
                default:
                    break;
            }
        }
        const nonNullNumber = numberWithUnit?.number != null ? String(numberWithUnit.number) : "";
        this.textboxNumber.text = DisplayValue.getDisplayValue(nonNullNumber, this.textboxNumber.displayType);
        this.textboxUnit.setSelectedItemFromValue(numberWithUnit?.unit.abbreviation);
        this.syncPrintableLabelText();
    }

    // Override so only the overall UnitInputField component can exist as a bound component
    public override updateBoundData(row: ModelRow, mode: DataSourceMode) {
        if (this.field == null || this.printable === true) {
            return;
        }

        // Ensure we have a NumberWithUnit object, and fill it with the updated number/unit values
        const numberWithUnit = this.getExistingValue(row);
        numberWithUnit.number = Number(NumberUtil.removeFormatting(this.textboxNumber.text));
        numberWithUnit.unit = this.textboxUnit.selectedItem?.value;

        if (mode === DataSourceMode.SEARCH) {
            numberWithUnit.searchUnits = this.textboxUnit.selectedItemsValues;
        }

        // Update the row
        switch (this.displayType) {
            case DisplayType.DISTANCE:
                row.updateDistance(this.field, numberWithUnit, this);
                break;
            case DisplayType.LENGTH:
                row.updateLength(this.field, numberWithUnit, this);
                break;
            case DisplayType.TEMPERATURE:
                row.updateTemperature(this.field, numberWithUnit, this);
                break;
            case DisplayType.VOLUME:
                row.updateVolume(this.field, numberWithUnit, this);
                break;
            case DisplayType.WEIGHT:
                row.updateWeight(this.field, numberWithUnit, this);
                break;
            default:
                break;
        }

        // Since we are updating the values as a group, null out any other fields that may have been sent individually
        // from the server.  (This is weird.)
        row.set(this.field + NumberWithUnitPeerField.UNIT, undefined);
        row.set(this.field + NumberWithUnitPeerField.FUNCTIONAL_NUMBER, undefined);
        row.set(this.field + NumberWithUnitPeerField.FUNCTIONAL_UNIT, undefined);

        if (mode === DataSourceMode.SEARCH) {
            numberWithUnit.updateForSearch(!this.textboxNumber.isEmpty());
        }
    }

    private getExistingValue(row: ModelRow): NumberWithUnit {
        switch (this.displayType) {
            case DisplayType.DISTANCE:
                return row.getDistance(this.field, 0);
            case DisplayType.LENGTH:
                return row.getLength(this.field, 0);
            case DisplayType.TEMPERATURE:
                return row.getTemperature(this.field, 0);
            case DisplayType.VOLUME:
                return row.getVolume(this.field, 0);
            case DisplayType.WEIGHT:
                return row.getWeight(this.field, 0);
            default:
                return null;
        }
    }

    get addlValidationCallback(): (value: string, mode: DataSourceMode) => ValidationResult {
        return this._addlValidationCallback;
    }

    set addlValidationCallback(value: (value: string, mode: DataSourceMode) => ValidationResult) {
        this._addlValidationCallback = value;
    }

    // Override so only the overall UnitInputField component can exist as a bound component
    override validate(checkRequired: boolean, showErrors: boolean = true): ValidationResult[] {
        if (this.printable) {
            return null;
        }
        const numberValidationResults = this.textboxNumber.validate(checkRequired, showErrors) ?? [];
        const unitValidationResults = this.textboxUnit.validate(checkRequired, showErrors) ?? [];
        const allResults: ValidationResult[] = [ ...numberValidationResults, ...unitValidationResults ];
        const invalidResults = ValidationUtil.returnInvalidResults(allResults);
        if (showErrors) {
            if (ArrayUtil.isEmptyArray(invalidResults)) {
                this.validationWarnings = null;
            } else {
                this.validationWarnings = invalidResults.map(validationResult => validationResult.validationMessage);
            }
        }
        return invalidResults;
    }

    get validationWarnings(): string[] {
        return this._validationWarnings;
    }

    set validationWarnings(value: string[]) {
        if (this._validationWarnings === value) {
            return;
        }
        this._validationWarnings = value;
        this.syncValidationWarningProps();
        this.syncTooltipMouseListeners();
    }

    override resetValidation() {
        this.validationWarnings = null;
    }

    private syncValidationWarningProps() {
        if (this.validationWarningProps != null) {
            this.revertTempState();
            this.validationWarningProps = null;
        }

        if (!StringUtil.isEmptyString(this._validationWarnings)) {
            this.validationWarningProps = {
                borderWidth: 1,
                borderColor: getThemeColor("error")
            };

            this.applyTempState(this.validationWarningProps);
        }
    }

    public get width(): string | number {
        return super.width ?? this.getPropertyDefinitions().width.defaultValue;
    }

    public set width(value: string | number) {
        super.width = value;
    }

    override getPropertyDefinitions(): ComponentPropDefinitions {
        return UnitInputFieldPropDefinitions.getDefinitions();
    }

    override get serializationName() {
        return "unitinputfield";
    }

    override get properName(): string {
        return "Unit Input Field";
    }

    protected override determinePropertyDefaultValue(prop: ComponentPropDefinition): any {
        if (prop.name === "unitDropdownWidth") {
            return this.defaultUnitDropdownWidth;
        }
        return super.determinePropertyDefaultValue(prop);
    }

    ///////////////////////
    // Number Properties //
    ///////////////////////
    get numberClearButtonVisible(): ClearButtonVisible {
        return this._numberClearButtonVisible == null ? this.getPropertyDefinitions().numberClearButtonVisible.defaultValue : this._numberClearButtonVisible;
    }

    set numberClearButtonVisible(value: ClearButtonVisible) {
        this.textboxNumber.clearButtonVisible = value;
    }

    get numberMaxValue(): number {
        return this.textboxNumber.maxValue;
    }

    set numberMaxValue(value: number) {
        this.textboxNumber.maxValue = value;
    }

    get numberMinValue(): number {
        return this.textboxNumber.minValue;
    }

    set numberMinValue(value: number) {
        this.textboxNumber.minValue = value;
    }

    get numberPlaceholder(): string {
        return this.textboxNumber.placeholder;
    }

    set numberPlaceholder(value: string) {
        this.textboxNumber.placeholder = value;
    }

    get numberPlaceholderColor(): Color {
        return this.textboxNumber.placeholderColor;
    }

    set numberPlaceholderColor(value: Color) {
        this.textboxNumber.placeholderColor = value;
    }

    get numberText(): string {
        return this.textboxNumber.text;
    }

    set numberText(value: string) {
        this.textboxNumber.text = value;
    }

    /////////////////////
    // Unit Properties //
    /////////////////////
    get unitAllowDropdownBlank(): boolean {
        return this._unitAllowDropdownBlank == null ?
            this.getPropertyDefinitions().unitAllowDropdownBlank.defaultValue :
            this._unitAllowDropdownBlank;
    }

    set unitAllowDropdownBlank(value: boolean) {
        this.textboxUnit.allowDropdownBlank = value;
    }

    public get unitAllowDropdownMultiSelect(): boolean {
        return this.textboxUnit.allowDropdownMultiSelect;
    }

    public set unitAllowDropdownMultiSelect(value: boolean) {
        this.textboxUnit.allowDropdownMultiSelect = value;
    }

    public get unitDropdownWidth(): number {
        return this.textboxUnit.dropdownWidth;
    }

    public set unitDropdownWidth(value: number) {
        this.textboxUnit.dropdownWidth = value;
    }

    private get defaultUnitDropdownWidth(): number {
        switch (this.displayType) {
            case DisplayType.DISTANCE:
                return 145;
            case DisplayType.LENGTH:
                return 115;
            case DisplayType.TEMPERATURE:
                return 145;
            case DisplayType.VOLUME:
                return 170;
            case DisplayType.WEIGHT:
                return 135;
            default:
                return null;
        }
    }

    get unitField(): string {
        return this.textboxUnit.field;
    }

    get unitItems(): TextboxItemsSource {
        return this.textboxUnit.items;
    }

    set unitItems(value: TextboxItemsSource) {
        this.textboxUnit.items = value;
    }

    get selectedUnit(): DropdownItem {
        return this.textboxUnit.selectedItem;
    }

    set selectedUnit(value: DropdownItem) {
        this.textboxUnit.selectedItem = value;
    }

    get selectedUnits(): DropdownItem[] {
        return this.textboxUnit.selectedItems;
    }

    set selectedUnits(value: DropdownItem[]) {
        this.textboxUnit.selectedItems = value;
    }

    get selectedUnitsValues(): string[] {
        return this.textboxUnit.selectedItemsValues;
    }

    get selectedUnitsDisplayValues(): string[] {
        return this.textboxUnit.selectedItemsDisplayValues;
    }

    get selectedItemCount(): number {
        return this.textboxUnit.selectedItemCount;
    }

    public setSelectedUnitFromValue(value: string) {
        this.textboxUnit.setSelectedItemFromValue(value);
    }

    public setSelectedUnitFromDisplayValue(displayValue: string) {
        this.textboxUnit.setSelectedItemFromDisplayValue(displayValue);
    }

    get unitWidth(): string|number {
        return this.textboxUnit.width ?? this.getPropertyDefinitions().unitWidth.defaultValue;
    }

    set unitWidth(value: string|number) {
        this.textboxUnit.width = value;
    }

    ////////////
    // Events //
    ////////////
    public addNumberFocusListener(value: FocusListener): UnitInputField {
        this.textboxNumber.addFocusListener(value);
        return this;
    }

    public removeNumberFocusListener(value: FocusListener): UnitInputField {
        this.textboxNumber.removeFocusListener(value);
        return this;
    }

    public addNumberBlurListener(value: BlurListener): UnitInputField {
        this.textboxNumber.addBlurListener(value);
        return this;
    }

    public removeNumberBlurListener(value: BlurListener): UnitInputField {
        this.textboxNumber.removeBlurListener(value);
        return this;
    }

    public addNumberKeyDownListener(value: KeyListener): UnitInputField {
        this.textboxNumber.addKeyDownListener(value);
        return this;
    }

    public removeNumberKeyDownListener(value: KeyListener): UnitInputField {
        this.textboxNumber.removeKeyDownListener(value);
        return this;
    }

    public addNumberKeyUpListener(value: KeyListener): UnitInputField {
        this.textboxNumber.addKeyUpListener(value);
        return this;
    }

    public removeNumberKeyUpListener(value: KeyListener): UnitInputField {
        this.textboxNumber.removeKeyUpListener(value);
        return this;
    }

    public addUnitBeforeSelectionListener(value: DropdownSelectionListener): UnitInputField {
        this.textboxUnit.addBeforeDropdownSelectionListener(value);
        return this;
    }

    public removeUnitBeforeSelectionListener(value: DropdownSelectionListener): UnitInputField {
        this.textboxUnit.removeBeforeDropdownSelectionListener(value);
        return this;
    }

    public addUnitAfterSelectionListener(value: DropdownSelectionListener): UnitInputField {
        this.textboxUnit.addAfterDropdownSelectionListener(value);
        return this;
    }

    public removeUnitAfterSelectionListener(value: DropdownSelectionListener): UnitInputField {
        this.textboxUnit.removeAfterDropdownSelectionListener(value);
        return this;
    }

    public addNumberChangeListener(value: ChangeListener): UnitInputField {
        this.textboxNumber.addChangeListener(value);
        return this;
    }

    public removeNumberChangeListener(value: ChangeListener): UnitInputField {
        this.textboxNumber.removeChangeListener(value);
        return this;
    }

    /////////////////
    // Permissions //
    /////////////////
    get numberDisabledByServer(): boolean {
        return this._numberDisabledByServer ?? this.getPropertyDefinitions().numberDisabledByServer.defaultValue;
    }

    private set numberDisabledByServer(value: boolean) {
        this._numberDisabledByServer = value;
        this.syncEnabled();
    }

    get unitDisabledByServer(): boolean {
        return this._unitDisabledByServer ?? this.getPropertyDefinitions().unitDisabledByServer.defaultValue;
    }

    private set unitDisabledByServer(value: boolean) {
        this._unitDisabledByServer = value;
        this.syncEnabled();
    }

    get permissionsTypes(): PermissionsDefinition[] {
        const result = super.permissionsTypes;
        result.push({
            permsType: "unitInputFieldEditNumber",
            description: "Edit security - Number",
            availableToAllDescription: "Everyone can edit the number value",
            availableToNoneDescription: "No one can edit the number value",
        });
        result.push({
            permsType: "unitInputFieldEditUnit",
            description: "Edit security - Unit",
            availableToAllDescription: "Everyone can edit the unit value",
            availableToNoneDescription: "No one can edit the unit value",
        });
        return result;
    }

    //////////
    // MISC //
    //////////
    private applyToBothTextboxes(props: Partial<TextboxProps>) {
        this.bothTextboxes.forEach(textbox => textbox.setProps(props));
    }

    private get log(): Logger {
        if (this.logger == null) {
            this.logger = DesignableObjectLogManager.getLogger("components.unitinputfield.UnitInputField");
        }
        return this.logger;
    }
}

// Apply mixins at runtime
MixinUtil.applyMixins(UnitInputField, [ CaptionedMixin, PrintableMixin ]);

// TypeScript interface declaration for mixin integration
export interface UnitInputField extends CaptionedMixinInterface, PrintableMixinInterface {
}

ComponentTypes.registerComponentType("unitinputfield", { constructor: UnitInputField.prototype.constructor });
